import React, { useEffect, useMemo } from 'react';
import styled from '@emotion/styled/macro';
import ScaleStatementBox from './ScaleStatementBox';
import { Button } from './Button';
import { theme } from '@arnold/common';
import { useTranslation } from 'react-i18next';
import { IInputContextValue, InputConsumer } from '../chat/InputContext';
import { GetMessagesQuery, ScaleAnswer, ScaleQuestionDefinition } from '../generated/hooks';
import { uniq } from 'ramda';
import { hidePipeSymbols } from '../lib/questionText';
import ScaleRankingList from './ScaleRankingList';
import { createDeterministicOrder } from './scaleStatementOrder';

const StatementsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 32px;
  padding: 16px 0;
  width: 930px;
  max-width: 100%;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoint.small}) {
    gap: 24px;
    padding: 12px 0;
  }
`;

interface ScaleStatementsProps {
  statements: string[];
  choices: string[];
  onSubmit?: (selections: number[]) => void;
  disabled?: boolean;
  randomizeOrder?: boolean;
  isRankingScale?: boolean;
  showEscapeOption?: boolean;
  seedKey: string;
}

const StyledButton = styled(Button)`
  width: 100%;
  border-radius: 19px;
  padding: 8px 24px;
  font-size: ${theme.typography.body.medium.button.fontSize};
  font-weight: ${theme.typography.body.medium.button.fontWeight};
  color: ${theme.colors.textInverted.primary};
  background-color: ${theme.colors.actionPrimary.default};
  border-radius: 19px;
  padding: 8px 24px;
  cursor: pointer;
  &:disabled {
    background-color: ${theme.colors.backgroundBasic.disabled};
    color: ${theme.colors.actionPrimary.disabled};
    cursor: not-allowed;
  }
`;

const StatementWrapper = styled.div`
  width: 100%;
  padding: 0 12px;
`;

interface ScaleStatementsInnerProps extends ScaleStatementsProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  context: IInputContextValue;
}

const ScaleStatementsInner: React.FC<ScaleStatementsInnerProps> = ({
  statements,
  choices,
  onSubmit,
  disabled,
  randomizeOrder,
  showEscapeOption,
  seedKey,
  context,
}) => {
  const { state, initScaleState, setScaleSelection, setScaleComment, submitScaleAnswers } = context;
  const { t } = useTranslation('scaleStatements');

  const escapeOptionIndex = showEscapeOption ? choices.length : null;
  const choicesWithEscapeOption = showEscapeOption ? [...choices, t('bubble:scaleEscapeOption')] : choices;
  const statementOrder = useMemo(() => {
    if (!randomizeOrder || statements.length < 2) {
      return statements.map((_statement, index) => index);
    }
    return createDeterministicOrder(statements.length, seedKey);
  }, [randomizeOrder, seedKey, statements]);

  useEffect(() => {
    initScaleState(statements.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statements.length]);

  const doAllStatementsHaveChoices =
    state.scaleSelections.length === statements.length &&
    state.scaleSelections.every((selection: number | null) => selection !== null);

  const handleSubmit = async () => {
    if (onSubmit && doAllStatementsHaveChoices) {
      onSubmit(state.scaleSelections as number[]);
    }
    if (doAllStatementsHaveChoices) {
      await submitScaleAnswers();
    }
  };

  return (
    <StatementsContainer>
      {statementOrder.map((originalStatementIndex) => (
        <StatementWrapper key={`statement-container-${originalStatementIndex}`}>
          <ScaleStatementBox
            statement={hidePipeSymbols(statements[originalStatementIndex] ?? '')}
            choices={choicesWithEscapeOption}
            escapeOptionIndex={escapeOptionIndex}
            onChoiceChange={(choiceIndex) => setScaleSelection(originalStatementIndex, choiceIndex)}
            onCommentChange={(comment) => setScaleComment(originalStatementIndex, comment)}
            selectedChoice={state.scaleSelections[originalStatementIndex]}
            comment={state.scaleComments[originalStatementIndex] ?? null}
          />
        </StatementWrapper>
      ))}
      <StyledButton onClick={handleSubmit} disabled={!doAllStatementsHaveChoices || disabled}>
        {t('submit')}
      </StyledButton>
    </StatementsContainer>
  );
};

const ScaleStatements: React.FC<ScaleStatementsProps> = ({
  statements,
  choices,
  onSubmit,
  disabled,
  randomizeOrder,
  isRankingScale,
  showEscapeOption = true,
  seedKey,
}) => {
  if (isRankingScale) {
    return (
      <ScaleRankingList
        statements={statements}
        randomizeOrder={randomizeOrder}
        seedKey={seedKey}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <InputConsumer>
      {(context) => (
        <ScaleStatementsInner
          context={context}
          statements={statements}
          choices={choices}
          randomizeOrder={randomizeOrder}
          showEscapeOption={showEscapeOption}
          seedKey={seedKey}
          onSubmit={onSubmit}
          disabled={disabled}
        />
      )}
    </InputConsumer>
  );
};

export default ScaleStatements;

export const orderScaleAnswersInMessages = (messages: GetMessagesQuery['messages']): GetMessagesQuery['messages'] => {
  if (!messages) {
    return messages;
  }
  const scaleAnswerQuestionDefinitionIds = uniq(
    messages.filter(shouldOrderScaleAnswers).map((m) => m.question.definition.id),
  );
  if (scaleAnswerQuestionDefinitionIds.length === 0) {
    return messages;
  }
  const messagesCopy = [...messages];
  for (const qDefId of scaleAnswerQuestionDefinitionIds) {
    const messagesForQDef = messagesCopy
      .filter(shouldOrderScaleAnswers)
      .filter((message) => message.question.definition.id === qDefId);
    const questionMessage = messagesCopy.find(
      (message): message is ScaleQuestionMessage => isScaleQuestionMessage(message) && message.definition.id === qDefId,
    );
    if (!questionMessage) {
      continue;
    }
    const newMessageOrder = questionMessage.definition.isRankingScale
      ? [...messagesForQDef].sort(
          (first, second) => (first.value[0] ?? Number.MAX_SAFE_INTEGER) - (second.value[0] ?? Number.MAX_SAFE_INTEGER),
        )
      : createDeterministicOrder(messagesForQDef.length, questionMessage.id).map((index) => messagesForQDef[index]);
    const originalIndexesOfMessages = messagesForQDef.map((m) => messagesCopy.indexOf(m));
    if (originalIndexesOfMessages.some((index) => index === -1)) {
      console.error('Message not found in messagesCopy');
      continue;
    }
    // check that originalIndexesOfMessages are in ascending order - for example 5,6,7,8
    let lastIndex = originalIndexesOfMessages[0];
    let invalidOrderFound = false;
    for (let i = 1; i < originalIndexesOfMessages.length; i++) {
      if (originalIndexesOfMessages[i] !== lastIndex + 1) {
        console.error('Messages are not in ascending order');
        invalidOrderFound = true;
        break;
      }
      lastIndex = originalIndexesOfMessages[i];
    }
    if (invalidOrderFound) {
      continue;
    }
    for (let i = 0; i < originalIndexesOfMessages.length; i++) {
      messagesCopy[originalIndexesOfMessages[i]] = newMessageOrder[i];
    }
  }
  return messagesCopy;
};

const shouldOrderScaleAnswers = (message: NonNullable<GetMessagesQuery['messages']>[0]): message is ScaleAnswer =>
  message.__typename === 'ScaleAnswer' &&
  message.question.definition.__typename === 'ScaleQuestionDefinition' &&
  (message.question.definition.randomizeOrder || message.question.definition.isRankingScale);

type ScaleQuestionMessage = Extract<NonNullable<GetMessagesQuery['messages']>[0], { __typename?: 'Question' }> & {
  definition: ScaleQuestionDefinition;
};

const isScaleQuestionMessage = (
  message: NonNullable<GetMessagesQuery['messages']>[0],
): message is ScaleQuestionMessage =>
  message.__typename === 'Question' && message.definition.__typename === 'ScaleQuestionDefinition';
